import React, { useState } from 'react';
import { Card, Button, Input, Space, Tag, Table, Alert, Spin, Typography, Divider } from 'antd';
import { 
  CheckCircleOutlined, CloseCircleOutlined, WarningOutlined,
  SearchOutlined, ReloadOutlined
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { formatCheckoutEvidenceSummary } from '../../features/preauth/paDisplayUtils';
import CheckoutImplications from './CheckoutImplications';
import { notification } from 'antd';

const { Title, Text } = Typography;

/**
 * Admin Checkout Validation Status Checker Component
 * Allows admins to check validation status for any checkin
 */
const AdminCheckoutValidationChecker = () => {
  const [checkinId, setCheckinId] = useState('');
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkValidation = async () => {
    if (!checkinId.trim()) {
      notification.warning({
        message: 'Checkin ID Required',
        description: 'Please enter a checkin ID',
      });
      return;
    }

    setLoading(true);
    setValidation(null);
    
    try {
      const response = await lib.getCheckoutValidation(checkinId.trim());
      
      if (response?.data?.error) {
        notification.error({
          message: 'Validation Check Failed',
          description: response.data.message || 'Failed to check validation status',
        });
        setValidation(null);
      } else {
        // The verdict `code` sits on the envelope, next to `data` — keep it, it
        // is the only thing separating the all-PA-rejected delete from the other
        // outcomes that also report validation_status "all_pa_items_rejected"
        // or can_checkout: true.
        const body = response?.data;
        const payload = body?.data || body;
        setValidation(
          payload && typeof payload === 'object' && !Array.isArray(payload)
            ? { ...payload, code: payload.code ?? body?.code ?? null }
            : payload
        );
      }
    } catch (error) {
      console.error('Error checking validation:', error);
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to check validation status',
      });
      setValidation(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, canCheckout, code) => {
    // Checked before can_checkout: the API allows checkout here, but submitting
    // deletes the encounter instead of serving it as a claim, so a plain green
    // "Can Checkout" badge would misread the verdict.
    const codeMap = {
      'ALL_PA_ITEMS_REJECTED_SOFT_DELETE': {
        color: 'error',
        text: 'All PA Items Rejected - Checkout Deletes the Encounter',
        icon: <WarningOutlined />,
      },
    };
    if (codeMap[code]) {
      const codeConfig = codeMap[code];
      return (
        <Tag icon={codeConfig.icon} color={codeConfig.color} style={{ fontSize: '14px', padding: '4px 12px' }}>
          {codeConfig.text}
        </Tag>
      );
    }

    if (canCheckout) {
      return (
        <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: '14px', padding: '4px 12px' }}>
          Valid - Can Checkout
        </Tag>
      );
    }

    const statusMap = {
      'no_pa_items': { color: 'error', text: 'No PA Items', icon: <CloseCircleOutlined /> },
      'no_approved_pa_items': { color: 'warning', text: 'No Approved PA Items', icon: <WarningOutlined /> },
      'all_pa_items_rejected': { color: 'warning', text: 'All PA Items Rejected', icon: <WarningOutlined /> },
      'valid': { color: 'success', text: 'Valid', icon: <CheckCircleOutlined /> },
    };

    const config = statusMap[status] || { color: 'default', text: status || 'Unknown', icon: <WarningOutlined /> };
    
    return (
      <Tag icon={config.icon} color={config.color} style={{ fontSize: '14px', padding: '4px 12px' }}>
        {config.text}
      </Tag>
    );
  };

  const paStatusColumns = [
    {
      title: 'Metric',
      dataIndex: 'label',
      key: 'label',
      width: 200,
    },
    {
      title: 'Count',
      dataIndex: 'value',
      key: 'value',
      width: 100,
      align: 'center',
      render: (value, record) => (
        <Tag color={record.color} style={{ fontSize: '14px', padding: '4px 12px' }}>
          {value}
        </Tag>
      ),
    },
  ];

  const paStatusData = validation?.pa_status ? [
    {
      key: 'total',
      label: 'Total Items',
      value: validation.pa_status.total_items || 0,
      color: 'default',
    },
    {
      key: 'approved',
      label: 'Approved',
      value: validation.pa_status.approved_items || 0,
      color: 'success',
    },
    {
      key: 'pending',
      label: 'Pending',
      value: validation.pa_status.pending_items || 0,
      color: 'processing',
    },
    {
      key: 'rejected',
      label: 'Rejected',
      value: validation.pa_status.rejected_items || 0,
      color: 'error',
    },
  ] : [];

  // The all-PA-rejected verdict reports can_checkout: true but deletes the
  // encounter on submit. Keyed off the envelope code so the panel does not read
  // as an ordinary green pass.
  const deletesOnCheckout =
    validation?.code === 'ALL_PA_ITEMS_REJECTED_SOFT_DELETE';
  const statusAlertType = deletesOnCheckout
    ? 'error'
    : validation?.can_checkout
      ? 'success'
      : 'warning';

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SearchOutlined />
          <span>Checkout Validation Status</span>
        </div>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Input Section */}
        <div>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            Checkin ID
          </Text>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Enter checkin ID (e.g., FAC/2025/01/00123)"
              value={checkinId}
              onChange={(e) => setCheckinId(e.target.value)}
              onPressEnter={checkValidation}
              allowClear
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={checkValidation}
              loading={loading}
            >
              Check Status
            </Button>
          </Space.Compact>
        </div>

        {/* Validation Result */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        )}

        {validation && !loading && (
          <div>
            <Divider orientation="left">Validation Result</Divider>
            
            {/* Status Alert */}
            <Alert
              message={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>
                    <Text strong>Validation Status: </Text>
                    {getStatusBadge(validation.validation_status, validation.can_checkout, validation.code)}
                  </span>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Checkin: {validation.checkin_id || checkinId}
                  </Text>
                </div>
              }
              type={statusAlertType}
              showIcon
              style={{ marginBottom: '16px' }}
            />

            {/* Evidence status */}
            {formatCheckoutEvidenceSummary(validation) && (
              <Alert
                type={validation.can_checkout ? 'info' : 'warning'}
                message="PA evidence"
                description={formatCheckoutEvidenceSummary(validation)}
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            {/* PA Status Table */}
            {validation.pa_status && (
              <div style={{ marginBottom: '16px' }}>
                <Title level={5} style={{ marginBottom: '12px' }}>
                  PA Status Breakdown
                </Title>
                <Table
                  columns={paStatusColumns}
                  dataSource={paStatusData}
                  pagination={false}
                  size="small"
                  bordered
                />
              </div>
            )}

            {/* What an admin checkout would do to this encounter (server-authored) */}
            {validation.implications && (
              <div style={{ marginBottom: '16px' }}>
                <Title level={5} style={{ marginBottom: '12px' }}>
                  <WarningOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                  If you check this encounter out
                </Title>
                <CheckoutImplications implications={validation.implications} />
              </div>
            )}

            {/* Warnings */}
            {validation.warnings && validation.warnings.length > 0 && (
              <div>
                <Title level={5} style={{ marginBottom: '12px' }}>
                  <WarningOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                  Warnings
                </Title>
                <Alert
                  message={
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {validation.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  }
                  type="warning"
                  showIcon
                />
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={checkValidation}
                loading={loading}
              >
                Refresh
              </Button>
            </div>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default AdminCheckoutValidationChecker;

